import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

/** Matches ProvablyFairInfoModal / ProvablyFair round modal timing. */
const OVERLAY_FADE_MS = 250;
const PANEL_TRANSITION =
  'transform 0.5s cubic-bezier(0.25, 1, 0.3, 1), opacity 0.4s ease';

export function useMenuOverlaySlideIn(isOpen: boolean) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => setIsAnimatingIn(true), 10);
    } else if (shouldRender) {
      setIsAnimatingIn(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
      }, 500);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen, shouldRender]);

  const getOverlayStyle = useCallback(
    (base: CSSProperties): CSSProperties => ({
      ...base,
      opacity: isAnimatingIn ? 1 : 0,
      transition: `opacity ${OVERLAY_FADE_MS}ms ease`,
    }),
    [isAnimatingIn]
  );

  const getPanelStyle = useCallback(
    (base: CSSProperties): CSSProperties => ({
      ...base,
      transform: isAnimatingIn ? 'translateY(0)' : 'translateY(-100vh)',
      opacity: isAnimatingIn ? 1 : 0,
      transition: PANEL_TRANSITION,
    }),
    [isAnimatingIn]
  );

  return { shouldRender, getOverlayStyle, getPanelStyle };
}
